/**
 * Navigation Configuration
 *
 * Production sidebar: every visible item is a real, clickable route.
 */

export interface RouteConfig {
  path: string;
  label: string;
  labelKey: string;
  icon: string;
  group: string;
  implemented: boolean;
  parentPath?: string;
  showInSidebar?: boolean;
}

export interface NavigationGroup {
  id: string;
  label: string;
  labelKey: string;
  routes: RouteConfig[];
}

export const ROUTES: Record<string, RouteConfig> = {
  dashboard: {
    path: "/dashboard",
    label: "Dashboard",
    labelKey: "nav.dashboard",
    icon: "dashboard",
    group: "overview",
    implemented: true,
    showInSidebar: true,
  },
  projects: {
    path: "/projects",
    label: "Projects",
    labelKey: "nav.projects",
    icon: "folder",
    group: "projects-audits",
    implemented: true,
    showInSidebar: true,
  },
  audits: {
    path: "/audits",
    label: "Audit Runs",
    labelKey: "nav.audits",
    icon: "play",
    group: "projects-audits",
    implemented: true,
    showInSidebar: true,
  },
  auditDetails: {
    path: "/audit/:auditId",
    label: "Audit Details",
    labelKey: "nav.auditDetails",
    icon: "document",
    group: "projects-audits",
    implemented: true,
    parentPath: "/audits",
    showInSidebar: false,
  },
  findings: {
    path: "/findings",
    label: "Findings",
    labelKey: "nav.findings",
    icon: "bug",
    group: "investigation",
    implemented: true,
    showInSidebar: true,
  },
  findingDetails: {
    path: "/finding/:findingId",
    label: "Finding Details",
    labelKey: "nav.findingDetails",
    icon: "document",
    group: "investigation",
    implemented: true,
    parentPath: "/findings",
    showInSidebar: false,
  },
  evidence: {
    path: "/evidence",
    label: "Evidence",
    labelKey: "nav.evidence",
    icon: "code",
    group: "investigation",
    implemented: true,
    showInSidebar: true,
  },
  scores: {
    path: "/scores",
    label: "Scores",
    labelKey: "nav.scores",
    icon: "chart",
    group: "analysis",
    implemented: true,
    showInSidebar: true,
  },
  reports: {
    path: "/reports",
    label: "Reports",
    labelKey: "nav.reports",
    icon: "document-text",
    group: "analysis",
    implemented: true,
    showInSidebar: true,
  },
  fixWithAi: {
    path: "/fix-with-ai",
    label: "Fix with AI",
    labelKey: "nav.fixWithAi",
    icon: "sparkles",
    group: "ai-automation",
    implemented: true,
    showInSidebar: true,
  },
  reAudit: {
    path: "/re-audit",
    label: "Re-audit",
    labelKey: "nav.reAudit",
    icon: "refresh",
    group: "ai-automation",
    implemented: true,
    showInSidebar: true,
  },
  knowledgeBase: {
    path: "/knowledge-base",
    label: "Knowledge Base",
    labelKey: "nav.knowledgeBase",
    icon: "book",
    group: "knowledge",
    implemented: true,
    showInSidebar: true,
  },
  knowledgeControl: {
    path: "/knowledge-control",
    label: "Knowledge Control",
    labelKey: "nav.knowledgeControl",
    icon: "cog",
    group: "knowledge",
    implemented: true,
    showInSidebar: true,
  },
  knowledgePacks: {
    path: "/knowledge-control/packs",
    label: "Knowledge Packs",
    labelKey: "nav.knowledgePacks",
    icon: "book",
    group: "knowledge",
    implemented: true,
    showInSidebar: true,
  },
  knowledgePackDetail: {
    path: "/knowledge-control/packs/:packId",
    label: "Pack Control",
    labelKey: "nav.packControl",
    icon: "document",
    group: "knowledge",
    implemented: true,
    parentPath: "/knowledge-control/packs",
    showInSidebar: false,
  },
  knowledgeControlDetail: {
    path: "/knowledge-control/:ruleId",
    label: "Rule Control",
    labelKey: "nav.ruleControl",
    icon: "document",
    group: "knowledge",
    implemented: true,
    parentPath: "/knowledge-control",
    showInSidebar: false,
  },
  settings: {
    path: "/settings",
    label: "Settings",
    labelKey: "nav.settings",
    icon: "cog",
    group: "admin",
    implemented: true,
    showInSidebar: true,
  },
};

export const NAVIGATION_GROUPS: NavigationGroup[] = [
  {
    id: "overview",
    label: "Overview",
    labelKey: "group.overview",
    routes: [ROUTES.dashboard],
  },
  {
    id: "projects-audits",
    label: "Projects & Audits",
    labelKey: "group.projectsAudits",
    routes: [ROUTES.projects, ROUTES.audits],
  },
  {
    id: "investigation",
    label: "Investigation",
    labelKey: "group.investigation",
    routes: [ROUTES.findings, ROUTES.evidence],
  },
  {
    id: "analysis",
    label: "Analysis",
    labelKey: "group.analysis",
    routes: [ROUTES.scores, ROUTES.reports],
  },
  {
    id: "ai-automation",
    label: "AI & Automation",
    labelKey: "group.ai",
    routes: [ROUTES.fixWithAi, ROUTES.reAudit],
  },
  {
    id: "knowledge",
    label: "Knowledge",
    labelKey: "group.knowledge",
    routes: [ROUTES.knowledgeBase, ROUTES.knowledgeControl, ROUTES.knowledgePacks],
  },
  {
    id: "admin",
    label: "Admin",
    labelKey: "group.admin",
    routes: [ROUTES.settings],
  },
];

export function isRouteActive(routePath: string, currentPath: string): boolean {
  if (routePath === currentPath) {
    return true;
  }

  const routeRegex = new RegExp(
    "^" + routePath.replace(/:[^/]+/g, "[^/]+") + "$"
  );
  if (routeRegex.test(currentPath)) {
    return true;
  }

  const currentRoute = Object.values(ROUTES).find((r) => {
    if (r.path === currentPath) {
      return true;
    }
    const currentRouteRegex = new RegExp(
      "^" + r.path.replace(/:[^/]+/g, "[^/]+") + "$"
    );
    return currentRouteRegex.test(currentPath);
  });

  if (currentRoute && currentRoute.parentPath === routePath) {
    return true;
  }

  return false;
}

export function getBreadcrumbs(currentPath: string): RouteConfig[] {
  let currentRoute: RouteConfig | undefined;
  for (const route of Object.values(ROUTES)) {
    if (route.path === currentPath) {
      currentRoute = route;
      break;
    }

    const routeRegex = new RegExp(
      "^" + route.path.replace(/:[^/]+/g, "[^/]+") + "$"
    );
    if (routeRegex.test(currentPath)) {
      currentRoute = route;
      break;
    }
  }

  if (!currentRoute) {
    return [];
  }

  const trail: RouteConfig[] = [currentRoute];
  let parent = currentRoute.parentPath;

  while (parent) {
    const parentRoute = Object.values(ROUTES).find((r) => r.path === parent);
    if (parentRoute) {
      trail.unshift(parentRoute);
      parent = parentRoute.parentPath;
    } else {
      break;
    }
  }

  return trail;
}

export function getImplementedRoutes(): RouteConfig[] {
  return Object.values(ROUTES).filter((route) => route.implemented);
}

export function getPlaceholderRoutes(): RouteConfig[] {
  return Object.values(ROUTES).filter((route) => !route.implemented);
}

export function getSidebarRoutes(): RouteConfig[] {
  return Object.values(ROUTES).filter((route) => route.showInSidebar !== false);
}

export function getAllRoutes(): RouteConfig[] {
  return Object.values(ROUTES);
}
